import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import * as React from "react";

import { Checkbox } from "@replohq/lattice/components/checkbox/Checkbox";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@replohq/lattice/components/field/Field";
import { RadioGroupItem } from "@replohq/lattice/components/radio-group/RadioGroup";
import { Switch } from "@replohq/lattice/components/switch/Switch";
import { cn } from "@replohq/lattice/utils/cn";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

interface SelectionItemBaseProps<ClassName extends string = string> {
  id: string;
  label: React.ReactNode;
  variant?: "default" | "outline" | "ghost";
  description?: React.ReactNode;
  className?: LayoutClassName<ClassName>;
  disabled?: boolean;
  UNSAFE_className?: string;
}

type SelectionItemProps<ClassName extends string = string> =
  SelectionItemBaseProps<ClassName> &
    (
      | {
          type: "checkbox" | "switch";
          checked?: boolean;
          onCheckedChange?: (checked: boolean) => void;
        }
      | {
          type: "radio";
        }
    );

function SelectionItem<ClassName extends string>({
  type,
  id,
  label,
  variant = "default",
  description,
  className,
  disabled,
  UNSAFE_className,
  ...props
}: SelectionItemProps<ClassName>) {
  const component = exhaustiveSwitch({ type })({
    checkbox: () => (
      <Checkbox id={id} name={id} disabled={disabled} {...props} />
    ),
    switch: () => (
      <Switch id={id} name={id} size="sm" disabled={disabled} {...props} />
    ),
    radio: () => <RadioGroupItem value={id} id={id} disabled={disabled} />,
  });

  return (
    <FieldLabel
      htmlFor={id}
      UNSAFE_className={cn(
        variant === "outline" &&
          "cursor-pointer border border-border rounded-lg p-3 bg-surface hover:bg-selectable-hover has-data-checked:border-component-border has-data-checked:bg-sheet w-full! min-w-0",
        variant === "ghost" &&
          "cursor-pointer w-full! min-w-0 rounded-lg p-3 transition-colors hover:bg-selectable-hover has-[>[data-slot=field]]:border-transparent has-data-checked:border-transparent has-data-checked:bg-selectable-selected has-data-checked:hover:bg-selectable-selected-hover",
        disabled && "opacity-50 cursor-not-allowed",
        className,
        UNSAFE_className,
      )}
    >
      <Field
        orientation="horizontal"
        className={cn(
          "gap-3",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        {type !== "switch" && component}
        <FieldContent className="min-w-0">
          <FieldTitle>{label}</FieldTitle>
          {description && (
            <FieldDescription className="min-w-0">
              {description}
            </FieldDescription>
          )}
        </FieldContent>
        {type === "switch" && component}
      </Field>
    </FieldLabel>
  );
}

export { SelectionItem };
